import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import XGSPopup, { PopupTheme } from "../../../ui-components/xgs-popup/xgsPopup";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import {
  closeMethodPicker,
  paymentSelector,
  selectPaymentMethod,
} from "../../../slices/payment/paymentSlice";
import Amount from "../../../ui-components/molecules/amount/amount";
import XGSIcons from "../../../ui-components/icon/xgsIcons";
import XGSIcon from "../../../ui-components/icon/xgsIcon";
import { copyTextToClipboard } from "../../../hooks/utils";
import logo from "../../../images/logo-white.png";
import { PaymentMethodOption } from "../../../app/data/payment/PaymentSession";
import "./paymentMethodPicker.scss";

type SelectableMethod = "CREDIT_CARD" | "ECHECK" | "ACH";

const METHOD_ICONS: Record<SelectableMethod, typeof XGSIcons.faCreditCard> = {
  CREDIT_CARD: XGSIcons.faCreditCard,
  ECHECK: XGSIcons.faUniversity,
  ACH: XGSIcons.faExchangeAlt,
};

const buildReferenceMemo = (invoiceNumbers: number[]): string => {
  if (!invoiceNumbers.length) {
    return "";
  }
  if (invoiceNumbers.length === 1) {
    return `INV-${invoiceNumbers[0]}`;
  }
  return `INV-${invoiceNumbers[0]} +${invoiceNumbers.length - 1} more`;
};

const PaymentMethodPicker: React.FC = () => {
  const payment = useSelector(paymentSelector);
  const dispatch = useDispatch();
  const [selectedMethod, setSelectedMethod] = useState<SelectableMethod | null>(null);

  const enabledMethods = useMemo(
    () => (payment.availableMethods || []).filter((m) => m.enabled),
    [payment.availableMethods]
  );

  useEffect(() => {
    if (!payment.showMethodPicker) {
      return;
    }
    const recommended = enabledMethods.find((m) => m.recommended);
    const first = recommended || enabledMethods[0];
    setSelectedMethod(first ? (first.id as SelectableMethod) : null);
  }, [payment.showMethodPicker, enabledMethods]);

  const onClose = () => {
    dispatch(closeMethodPicker());
  };

  const invoiceNumbers = useMemo(
    () => payment.pendingInvoiceNumbers || [],
    [payment.pendingInvoiceNumbers]
  );
  const referenceMemo = useMemo(
    () => buildReferenceMemo(invoiceNumbers),
    [invoiceNumbers]
  );

  const invoiceSummary = useMemo(() => {
    const count = invoiceNumbers.length;
    if (count === 0) {
      return null;
    }
    const countLabel = count === 1 ? "1 Invoice" : `${count} Invoices`;
    const pills = invoiceNumbers.slice(0, 3).map((n) => `INV-${n}`);
    const remaining = count - pills.length;
    return { countLabel, pills, remaining };
  }, [invoiceNumbers]);

  const ach = payment.achBankDetails;
  const achRows = useMemo(
    () => [
      { label: "Bank Name", value: ach.bankName },
      { label: "Account Name", value: ach.accountName },
      { label: "Routing Number (ABA)", value: ach.routingNumber },
      { label: "Account Number", value: ach.accountNumber },
      { label: "Account Type", value: ach.accountType || "Checking" },
      { label: "Reference / Memo", value: referenceMemo },
    ],
    [ach, referenceMemo]
  );

  const selectedOption: PaymentMethodOption | undefined = enabledMethods.find(
    (m) => m.id === selectedMethod
  );

  const onCopyAll = () => {
    const text = achRows
      .filter((row) => row.value)
      .map((row) => `${row.label}: ${row.value}`)
      .join("\n");
    if (text) {
      copyTextToClipboard(text);
    }
  };

  const onPrimaryAction = () => {
    if (!selectedMethod) {
      return;
    }
    if (selectedMethod === "ACH") {
      onClose();
      return;
    }
    dispatch(selectPaymentMethod(selectedMethod));
  };

  return (
    <XGSPopup
      title=""
      opened={payment.showMethodPicker}
      onClose={onClose}
      className="payment-method-picker"
      size="680px"
      maxHeight="90vh"
      theme={PopupTheme.NEW}
      hideCloseButton
      closeOnDocumentClick={false}
    >
      <div className="xgs-payment-method-picker">
        <div className="xgs-payment-method-picker__header">
          <div className="xgs-payment-method-picker__brand">
            <img
              className="xgs-payment-method-picker__logo"
              src={logo}
              alt="XGS logo"
            />
            <h2 className="xgs-payment-method-picker__title">Pay Invoices</h2>
          </div>
          <button
            type="button"
            className="xgs-payment-method-picker__close"
            onClick={onClose}
            aria-label="Close"
          >
            <XGSIcon icon={XGSIcons.faTimes} />
          </button>
        </div>

        <div className="xgs-payment-method-picker__body">
          <div className="xgs-payment-method-picker__summary">
            <div className="xgs-payment-method-picker__summary-main">
              <div className="xgs-payment-method-picker__context-label">
                Amount due
              </div>
              <div className="xgs-payment-method-picker__amount">
                {payment.paymentSum != null && payment.paymentSum > 0 ? (
                  <Amount amount={payment.paymentSum} />
                ) : (
                  "—"
                )}
              </div>
              {invoiceSummary && (
                <div className="xgs-payment-method-picker__invoices">
                  <span>{invoiceSummary.countLabel}</span>
                  <span className="xgs-payment-method-picker__dot">·</span>
                  {invoiceSummary.pills.map((pill) => (
                    <span
                      key={pill}
                      className="xgs-payment-method-picker__invoice-pill"
                    >
                      {pill}
                    </span>
                  ))}
                  {invoiceSummary.remaining > 0 && (
                    <span className="xgs-payment-method-picker__invoice-pill">
                      +{invoiceSummary.remaining} more
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="xgs-payment-method-picker__security">
              <div className="xgs-payment-method-picker__secured">
                <XGSIcon icon={XGSIcons.faLock} />
                <span>Secured</span>
              </div>
              <div className="xgs-payment-method-picker__pci">
                <span className="xgs-payment-method-picker__pci-label">PCI</span>
                <span className="xgs-payment-method-picker__pci-badge">DSS</span>
              </div>
            </div>
          </div>

          <div className="xgs-payment-method-picker__section-label">
            Select payment method
          </div>

          <div className="xgs-payment-method-picker__methods">
            {enabledMethods.map((method) => {
              const id = method.id as SelectableMethod;
              const selected = selectedMethod === id;
              const feeBadge =
                id === "CREDIT_CARD" && method.surchargeEnabled ? "fee" : "none";
              return (
                <button
                  key={method.id}
                  type="button"
                  className={`xgs-payment-method-picker__method${
                    selected ? " xgs-payment-method-picker__method--selected" : ""
                  }`}
                  onClick={() => setSelectedMethod(id)}
                >
                  {selected && (
                    <span
                      className="xgs-payment-method-picker__check"
                      aria-hidden
                    >
                      <XGSIcon icon={XGSIcons.faCheckCircle} />
                    </span>
                  )}
                  <span
                    className={`xgs-payment-method-picker__fee-badge xgs-payment-method-picker__fee-badge--${feeBadge}`}
                  >
                    {feeBadge === "fee" ? "Fees may apply" : "No fees"}
                  </span>
                  <span className="xgs-payment-method-picker__method-icon">
                    <XGSIcon icon={METHOD_ICONS[id]} />
                  </span>
                  <span className="xgs-payment-method-picker__method-label">
                    {method.label}
                  </span>
                  {method.recommended && (
                    <span className="xgs-payment-method-picker__recommended">
                      Recommended
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {!!selectedOption?.displayMessage?.trim() && (
            <div className="xgs-payment-method-picker__disclosure">
              <XGSIcon icon={XGSIcons.faInfoCircle} />
              <span>{selectedOption.displayMessage}</span>
            </div>
          )}

          {selectedMethod === "CREDIT_CARD" &&
            selectedOption?.surchargeEnabled &&
            !!selectedOption?.surchargeWarning && (
              <div className="xgs-payment-method-picker__disclosure xgs-payment-method-picker__disclosure--warn">
                <XGSIcon icon={XGSIcons.faInfoCircle} />
                <span>{selectedOption.surchargeWarning}</span>
              </div>
            )}

          {selectedMethod === "ACH" && (
            <div className="xgs-payment-method-picker__ach">
              <div className="xgs-payment-method-picker__ach-banner">
                <span>
                  <XGSIcon icon={XGSIcons.faCheckCircle} />
                  Initiate a transfer from your bank.
                </span>
                <button
                  type="button"
                  className="xgs-payment-method-picker__copy-all"
                  onClick={onCopyAll}
                >
                  <XGSIcon icon={XGSIcons.faCopy} />
                  Copy All
                </button>
              </div>
              <div className="xgs-payment-method-picker__ach-fields">
                {achRows.map((row) => (
                  <div
                    key={row.label}
                    className="xgs-payment-method-picker__ach-row"
                  >
                    <div className="xgs-payment-method-picker__ach-row-main">
                      <div className="xgs-payment-method-picker__ach-label">
                        {row.label}
                      </div>
                      <div className="xgs-payment-method-picker__ach-value">
                        {row.value || "—"}
                      </div>
                    </div>
                    {!!row.value && (
                      <button
                        type="button"
                        className="xgs-payment-method-picker__ach-copy"
                        aria-label={`Copy ${row.label}`}
                        onClick={() => copyTextToClipboard(row.value)}
                      >
                        <XGSIcon icon={XGSIcons.faCopy} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {!!referenceMemo && (
                <div className="xgs-payment-method-picker__ach-memo-tip">
                  <XGSIcon icon={XGSIcons.faInfoCircle} />
                  <span>
                    Include <strong>{referenceMemo}</strong> in the transfer memo
                    for automatic matching.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="xgs-payment-method-picker__footer">
          <Button theme={ButtonThemes.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            theme={
              selectedMethod === "ACH" ? ButtonThemes.green : ButtonThemes.blue
            }
            onClick={onPrimaryAction}
            disabled={!selectedMethod}
          >
            <XGSIcon
              icon={
                selectedMethod === "ACH"
                  ? XGSIcons.faCheck
                  : XGSIcons.faLock
              }
            />{" "}
            {selectedMethod === "ACH"
              ? "Done — I'll Transfer Manually"
              : "Continue to Payment"}
          </Button>
        </div>
      </div>
    </XGSPopup>
  );
};

export default PaymentMethodPicker;
